// ============================================================================
// CFG-ELIG-001 — Eligibility Criteria Master (list + modal)
// ============================================================================
// Enterprise list pattern: a paginated grid of criteria sets (one row per
// case-type + product scope) with Add / View / Edit / Delete. The builder
// lives in EligibilityCriteriaFormModal. Criteria define which loan accounts
// become actionable per workflow; not yet wired into the live pool (E5).

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import IconSystem from 'icon-collection/icon-systems';
import { ColumnSelector, type ColumnDefinition } from 'components/common';
import { ConfirmDialog, ModuleHeader } from 'components/modals';
import type { ActionButton } from 'components/modals';
import {
  ActionMenu,
  type ActionMenuItem,
  SyutGrid,
  type SyutGridColumn,
  SyutSelect,
  MinimizedModalStrip,
} from 'components/ui';
import notificationService from 'services/notificationService';
import { useModalMinimize } from 'contexts/ModalMinimizeContext';
import { useCaseTypeOptions } from 'hooks/api/useCaseTypes';
import { useProducts } from 'hooks/api/useProducts';
import { useEligibilityCriteriaList } from 'hooks/api/useEligibilityCriteria';
import { eligibilityCriteriaService } from 'api/services/eligibilityCriteriaService';
import type {
  EligibilityCriteriaListItem,
  EligibilityCriteriaSearchRequest,
} from 'api/types/eligibilityCriteria.types';
import EligibilityCriteriaFormModal from 'components/modals/modules/eligibility-criteria-module/EligibilityCriteriaFormModal';

interface EligibilityCriteriaMasterScreenProps {
  tabId?: string;
  moduleName?: string;
}

const EligibilityCriteriaMasterScreen: React.FC<EligibilityCriteriaMasterScreenProps> = ({ tabId, moduleName }) => {
  const { options: caseTypeOptions, labels: caseTypeLabels } = useCaseTypeOptions();
  const { data: products } = useProducts({ pageSize: 100, sortBy: 'productCode', sortOrder: 'asc' });
  const { isMinimized, dismiss: dismissMinimized, getMinimizedModal } = useModalMinimize();

  const [queryParams, setQueryParams] = useState<EligibilityCriteriaSearchRequest>({ page: 1, pageSize: 25 });
  const { data, loading, pagination, refetch } = useEligibilityCriteriaList(queryParams);

  const [searchQuery, setSearchQuery] = useState('');
  const [filterOpen, setFilterOpen] = useState(false);
  const [caseTypeFilter, setCaseTypeFilter] = useState('');
  const [productFilter, setProductFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [includeHistory, setIncludeHistory] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => {
      setQueryParams(prev => ({ ...prev, searchTerm: searchQuery.trim() || undefined, page: 1 }));
    }, 400);
    return () => clearTimeout(t);
  }, [searchQuery]);

  const [formModalOpen, setFormModalOpen] = useState(false);
  const [formMode, setFormMode] = useState<'create' | 'edit' | 'view'>('create');
  const [selected, setSelected] = useState<EligibilityCriteriaListItem | null>(null);

  const createInstanceCounter = useRef(0);
  const [createInstanceId, setCreateInstanceId] = useState('0');
  const formModalMinimizeRef = useRef<(() => void) | null>(null);

  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [toDelete, setToDelete] = useState<EligibilityCriteriaListItem | null>(null);

  const [findOpen, setFindOpen] = useState(false);
  const [isFinding, setIsFinding] = useState(false);
  const [findTarget, setFindTarget] = useState<EligibilityCriteriaListItem | null>(null);

  const handleRestoreModal = useCallback(
    (restoredMode: 'create' | 'edit' | 'view', instanceId?: string, recordId?: string) => {
      if (instanceId) setCreateInstanceId(instanceId);
      if (recordId) {
        const rec = data.find(r => r.id === recordId);
        if (rec) setSelected(rec);
      }
      setFormMode(restoredMode);
      setFormModalOpen(true);
    },
    [data],
  );

  const handleAddNew = useCallback(() => {
    if (formModalOpen) formModalMinimizeRef.current?.();
    const newId = String(++createInstanceCounter.current);
    setCreateInstanceId(newId);
    setSelected(null);
    setFormMode('create');
    setFormModalOpen(true);
  }, [formModalOpen]);

  const handleEdit = useCallback((r: EligibilityCriteriaListItem) => {
    const modalId = tabId ? `${tabId}-form-modal-edit-${r.id}` : null;
    if (modalId && isMinimized(modalId)) {
      const m = getMinimizedModal(modalId); dismissMinimized(modalId); m?.onRestore?.(); return;
    }
    setSelected(r); setFormMode('edit'); setFormModalOpen(true);
  }, [tabId, isMinimized, dismissMinimized, getMinimizedModal]);

  const handleView = useCallback((r: EligibilityCriteriaListItem) => {
    setSelected(r); setFormMode('view'); setFormModalOpen(true);
  }, []);

  const handleDeleteClick = useCallback((r: EligibilityCriteriaListItem) => {
    setToDelete(r); setDeleteModalOpen(true);
  }, []);

  const handleFindEligibleClick = useCallback((r: EligibilityCriteriaListItem) => {
    setFindTarget(r); setFindOpen(true);
  }, []);

  const handleConfirmFindEligible = useCallback(async () => {
    if (!findTarget) return;
    setIsFinding(true);
    try {
      const res = await eligibilityCriteriaService.findEligible(findTarget.id);
      setIsFinding(false);
      setFindOpen(false);
      setFindTarget(null);
      notificationService.toast.success(
        `Added ${res.added} new actionable account${res.added === 1 ? '' : 's'} (scanned ${res.scanned}).`,
        6000,
      );
    } catch (err: any) {
      setIsFinding(false);
      notificationService.toast.error(err?.response?.data?.message || 'Failed to run eligibility.', 6000);
    }
  }, [findTarget]);

  const handleConfirmDelete = useCallback(async () => {
    if (!toDelete) return;
    setIsDeleting(true);
    try {
      await eligibilityCriteriaService.delete(toDelete.id);
      setIsDeleting(false);
      setDeleteModalOpen(false);
      notificationService.toast.success('Eligibility criteria deleted.');
      setToDelete(null);
      refetch();
    } catch (err: any) {
      setIsDeleting(false);
      notificationService.toast.error(err?.response?.data?.message || 'An error occurred while deleting.', 6000);
    }
  }, [toDelete, refetch]);

  const handlePageChange = useCallback((pageIndex: number) => {
    setQueryParams(prev => ({ ...prev, page: pageIndex + 1 }));
  }, []);
  const handlePageSizeChange = useCallback((pageSize: number) => {
    setQueryParams(prev => ({ ...prev, pageSize, page: 1 }));
  }, []);

  const handleApplyFilters = useCallback(() => {
    setQueryParams(prev => ({
      ...prev,
      caseTypeCode: caseTypeFilter || undefined,
      productId: productFilter || undefined,
      statusCode: statusFilter || undefined,
      includeHistory: includeHistory || undefined,
      page: 1,
    }));
    setFilterOpen(false);
  }, [caseTypeFilter, productFilter, statusFilter, includeHistory]);

  const handleClearFilters = useCallback(() => {
    setCaseTypeFilter(''); setProductFilter(''); setStatusFilter(''); setIncludeHistory(false);
    setQueryParams({ page: 1, pageSize: queryParams.pageSize ?? 25 });
  }, [queryParams.pageSize]);

  const activeFilterCount = useMemo(() => {
    let c = 0;
    if (queryParams.caseTypeCode) c++;
    if (queryParams.productId) c++;
    if (queryParams.statusCode) c++;
    if (queryParams.includeHistory) c++;
    return c;
  }, [queryParams]);

  const formatDate = (s?: string | null) => { if (!s) return '-'; try { return new Date(s).toLocaleDateString(); } catch { return s; } };

  const columns: SyutGridColumn<EligibilityCriteriaListItem>[] = useMemo(() => [
    { id: 'caseTypeCode', header: 'Case Type', accessorKey: 'caseTypeCode', size: 170, enableSorting: true,
      cell: ({ row }) => caseTypeLabels[row.original.caseTypeCode] || row.original.caseTypeCode },
    { id: 'productCode', header: 'Product Scope', accessorKey: 'productCode', size: 150,
      cell: ({ row }) => row.original.productCode || <span style={{ color: 'var(--text-muted)' }}>All products</span> },
    { id: 'matchMode', header: 'Match', accessorKey: 'matchMode', size: 90 },
    { id: 'conditionCount', header: 'Conditions', accessorKey: 'conditionCount', size: 100,
      cell: ({ row }) => row.original.conditionCount },
    { id: 'effectiveFrom', header: 'Effective From', accessorKey: 'effectiveFrom', size: 130, enableSorting: true,
      cell: ({ row }) => formatDate(row.original.effectiveFrom) },
    { id: 'effectiveTo', header: 'Effective To', accessorKey: 'effectiveTo', size: 130,
      cell: ({ row }) => formatDate(row.original.effectiveTo) },
    { id: 'description', header: 'Notes', accessorKey: 'description', size: 240, flex: true,
      cell: ({ row }) => row.original.description
        ? <span title={row.original.description} style={{ display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{row.original.description}</span>
        : <span style={{ color: 'var(--text-muted)' }}>—</span> },
    {
      id: 'statusCode', header: 'Status', accessorKey: 'statusCode', size: 110,
      cell: ({ row }) => {
        const c = row.original;
        const code = c.deletedAt ? 'DELETED' : (c.statusCode || 'ACTIVE');
        let color = '#6b7280';
        if (code === 'ACTIVE') color = '#16a34a';
        else if (code === 'DELETED') color = '#dc2626';
        return (
          <span style={{ fontSize: '0.75rem', fontWeight: 600, color, padding: '2px 8px', border: `1px solid ${color}`, borderRadius: '999px' }}>
            {code}
          </span>
        );
      },
    },
  ], [caseTypeLabels]);

  const columnDefinitions: ColumnDefinition[] = useMemo(
    () => columns.map(col => ({ id: col.id as string, label: typeof col.header === 'string' ? (col.header as string) : (col.id as string), defaultVisible: true })),
    [columns],
  );
  const [visibleColumns, setVisibleColumns] = useState<string[]>(() => columnDefinitions.map(c => c.id));
  const handleApplyColumns = useCallback((cols: string[]) => setVisibleColumns(cols), []);
  const handleResetColumns = useCallback(() => setVisibleColumns(columnDefinitions.map(c => c.id)), [columnDefinitions]);

  const getRowActions = useCallback((r: EligibilityCriteriaListItem): ActionMenuItem[] => [
    { id: 'find', label: 'Find Eligible', icon: <IconSystem.search size={16} />, onClick: () => handleFindEligibleClick(r) },
    { id: 'view', label: 'View', icon: <IconSystem.eye size={16} />, onClick: () => handleView(r) },
    { id: 'edit', label: 'Edit', icon: <IconSystem.edit size={16} />, onClick: () => handleEdit(r) },
    { id: 'delete', label: 'Delete', icon: <IconSystem.trash size={16} />, onClick: () => handleDeleteClick(r), variant: 'danger' },
  ], [handleView, handleEdit, handleDeleteClick, handleFindEligibleClick]);

  const actionButtons: ActionButton[] = useMemo(() => [
    { id: 'add', icon: <IconSystem.add size={18} />, label: 'Add', tooltip: 'Add eligibility criteria', onClick: handleAddNew, variant: 'icon' },
    { id: 'refresh', icon: <IconSystem.refresh size={18} />, label: 'Refresh', tooltip: 'Refresh', onClick: () => refetch(), variant: 'icon' },
  ], [handleAddNew, refetch]);

  return (
    <div className="module-master-container">
      <ModuleHeader
        title="Eligibility Criteria Master"
        description="Define which loan accounts become actionable (eligible to open a case) per workflow. Structured, effective-dated conditions."
        icon={<IconSystem.list size={18} />}
        searchConfig={{ value: searchQuery, onChange: setSearchQuery, placeholder: 'Search case type, product…' }}
        filterConfig={{ onClick: () => setFilterOpen(!filterOpen), isActive: filterOpen || activeFilterCount > 0, activeFilterCount }}
        leadingToolbarItems={tabId ? <MinimizedModalStrip tabId={tabId} /> : undefined}
        customToolbarItems={
          <ColumnSelector columns={columnDefinitions} visibleColumns={visibleColumns} onApply={handleApplyColumns} onReset={handleResetColumns} />
        }
        actions={actionButtons}
      />

      <div className="module-content" style={{ marginTop: '6px' }}>
        <div style={{ display: 'flex', gap: '0', flex: 1, overflow: 'hidden' }}>
          <div style={{ flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column', minHeight: 0 }}>
            <SyutGrid
              data={data}
              columns={columns}
              enableSorting
              enablePagination
              manualPagination
              totalCount={pagination.totalItems}
              currentPage={(queryParams.page ?? 1) - 1}
              pageSize={queryParams.pageSize ?? 25}
              onPageChange={handlePageChange}
              onPageSizeChange={handlePageSizeChange}
              visibleColumns={visibleColumns}
              rowActions={getRowActions}
              isLoading={loading}
              emptyMessage="No eligibility criteria configured yet"
            />
          </div>
        </div>

        {filterOpen && (
          <div className="search-panel">
            <div className="search-panel-header">
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.375rem' }}>
                <IconSystem.filter size={14} />
                <h3 className="search-panel-title">Filters</h3>
              </div>
              <button onClick={() => { handleClearFilters(); setFilterOpen(false); }}
                style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '0.25rem', display: 'flex', alignItems: 'center', color: 'var(--text-muted)' }}
                title="Close filters">
                <IconSystem.x size={16} />
              </button>
            </div>
            <div className="search-panel-content">
              <div className="filter-section">
                <SyutSelect label="Case Type" options={[{ value: '', label: 'All case types' }, ...caseTypeOptions]}
                  value={caseTypeFilter} onChange={(v) => setCaseTypeFilter((v as string) || '')} isClearable={false} size="sm" />
              </div>
              <div className="filter-section">
                <SyutSelect label="Product"
                  options={[{ value: '', label: 'All products' }, ...products.map(p => ({ value: p.id, label: p.productCode }))]}
                  value={productFilter} onChange={(v) => setProductFilter((v as string) || '')} isClearable={false} size="sm" />
              </div>
              <div className="filter-section">
                <SyutSelect label="Status"
                  options={[
                    { value: '', label: 'Active only (default)' },
                    { value: 'INACTIVE', label: 'Inactive only' },
                    { value: 'DELETED', label: 'Deleted only' },
                    { value: 'ALL', label: 'All (incl. deleted)' },
                  ]}
                  value={statusFilter} onChange={(v) => setStatusFilter((v as string) || '')} isClearable={false} size="sm" />
              </div>
              <div className="filter-section">
                <label style={{ display: 'inline-flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.8125rem', cursor: 'pointer' }}>
                  <input type="checkbox" checked={includeHistory} onChange={(e) => setIncludeHistory(e.target.checked)} />
                  Include past versions (history)
                </label>
              </div>
            </div>
            <div className="filter-actions">
              <button className="btn btn-secondary" onClick={handleClearFilters}>Clear</button>
              <button className="btn btn-primary" onClick={handleApplyFilters}>Apply</button>
            </div>
          </div>
        )}
      </div>

      <ConfirmDialog
        open={deleteModalOpen}
        title="Confirm Delete"
        message={toDelete ? `Delete the eligibility criteria for "${caseTypeLabels[toDelete.caseTypeCode] || toDelete.caseTypeCode}"${toDelete.productCode ? ` / ${toDelete.productCode}` : ' (all products)'}? Cases already opened from it are not affected.` : ''}
        confirmLabel="Delete"
        cancelLabel="Cancel"
        variant="danger"
        loading={isDeleting}
        loadingMessage="Deleting..."
        onConfirm={handleConfirmDelete}
        onCancel={() => { if (!isDeleting) { setDeleteModalOpen(false); setToDelete(null); } }}
      />

      <ConfirmDialog
        open={findOpen}
        title="Find eligible accounts?"
        message={findTarget
          ? `Run "${caseTypeLabels[findTarget.caseTypeCode] || findTarget.caseTypeCode}" eligibility against the current account snapshot. Only NEW accounts (not already in a case of this type) are added — existing cases are untouched.`
          : ''}
        confirmLabel="Find Eligible"
        cancelLabel="Cancel"
        variant="default"
        loading={isFinding}
        loadingMessage="Scanning accounts…"
        onConfirm={handleConfirmFindEligible}
        onCancel={() => { if (!isFinding) { setFindOpen(false); setFindTarget(null); } }}
      />

      <EligibilityCriteriaFormModal
        isOpen={formModalOpen}
        mode={formMode}
        record={selected}
        onSaved={() => refetch()}
        onClose={() => setFormModalOpen(false)}
        tabId={tabId}
        moduleName={moduleName}
        onRestoreModal={handleRestoreModal}
        createInstanceId={createInstanceId}
        minimizeRef={formModalMinimizeRef}
      />
    </div>
  );
};

export default EligibilityCriteriaMasterScreen;
